import { Button } from "../ui/button";
import { Card, CardContent } from "../ui/card";
import { Calendar } from "../ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import { toast } from "sonner";
import { format } from "date-fns";
import { CalendarIcon, Download, Send } from "lucide-react";
import { salaryApi, emailApi } from "../../lib/api";

export const FakturaunderlagTab = ({
  companies, selectedCompany, setSelectedCompany,
  companyEmail, setCompanyEmail, companyInvoice,
  ueStart, setUeStart, ueEnd, setUeEnd,
  loadCompanyInvoice,
}) => {
  return (
    <div className="space-y-6">
      <div className="flex flex-wrap gap-3 items-end">
        <div className="space-y-1">
          <label className="text-xs text-white/30">Foretag</label>
          <select value={selectedCompany} onChange={(e) => {
            setSelectedCompany(e.target.value);
            const comp = companies.find(c => c.name === e.target.value);
            setCompanyEmail(comp?.email || "");
            if (e.target.value) loadCompanyInvoice(e.target.value, ueStart, ueEnd);
          }}
            className="text-sm bg-[#080c12] border border-white/[0.06] text-white rounded-xl px-3 py-2" data-testid="company-select">
            <option value="">Valj foretag...</option>
            {companies.map(c => <option key={c.name} value={c.name}>{c.name}{c.email ? ` (${c.email})` : ''}</option>)}
          </select>
        </div>
        <div className="space-y-1">
          <label className="text-xs text-white/30">Fran</label>
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" className="w-[150px] justify-start text-left text-sm bg-[#080c12] border-white/[0.06] text-white hover:bg-white/[0.04] rounded-xl">
                <CalendarIcon className="mr-2 h-4 w-4 text-white/30" />
                {ueStart || "Valj datum"}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0 bg-[#0d1218] border-white/[0.06]" align="start">
              <Calendar mode="single" selected={ueStart ? new Date(ueStart) : undefined}
                onSelect={(d) => { if (d) setUeStart(format(d, 'yyyy-MM-dd')); }}
                className="rounded-xl" />
            </PopoverContent>
          </Popover>
        </div>
        <div className="space-y-1">
          <label className="text-xs text-white/30">Till</label>
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" className="w-[150px] justify-start text-left text-sm bg-[#080c12] border-white/[0.06] text-white hover:bg-white/[0.04] rounded-xl">
                <CalendarIcon className="mr-2 h-4 w-4 text-white/30" />
                {ueEnd || "Valj datum"}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0 bg-[#0d1218] border-white/[0.06]" align="start">
              <Calendar mode="single" selected={ueEnd ? new Date(ueEnd) : undefined}
                onSelect={(d) => { if (d) setUeEnd(format(d, 'yyyy-MM-dd')); }}
                className="rounded-xl" />
            </PopoverContent>
          </Popover>
        </div>
        <Button onClick={() => { if (selectedCompany) loadCompanyInvoice(selectedCompany, ueStart, ueEnd); }}
          className="bg-[#6dbf4a] hover:bg-[#5da83d] text-white" disabled={!selectedCompany}>
          Visa
        </Button>
        {companyInvoice && (
          <>
          <a href={`${salaryApi.getCompanyPdfUrl(selectedCompany, { start: ueStart || '', end: ueEnd || '', token: localStorage.getItem('token') })}`}
            target="_blank" rel="noopener noreferrer"
            className="flex items-center gap-1 px-3 py-2 text-sm bg-[#6dbf4a]/10 text-[#6dbf4a] rounded-xl hover:bg-[#6dbf4a]/20 transition-colors">
            <Download className="w-4 h-4" /> PDF
          </a>
          <button onClick={async () => {
            const email = prompt(`Skicka fakturaunderlag ${selectedCompany} till:`, companyEmail || '');
            if (!email) return;
            try {
              await emailApi.sendSpec({ recipient_email: email, subject: `Fakturaunderlag ${selectedCompany} ${companyInvoice.period_label}`, user_id: '', company: selectedCompany, start: ueStart, end: ueEnd, spec_type: 'company' });
              toast.success(`Skickat till ${email}`);
            } catch (e) { toast.error(e.response?.data?.detail || "Kunde inte skicka"); }
          }} className="flex items-center gap-1 px-3 py-2 text-sm bg-blue-500/10 text-blue-400 rounded-xl hover:bg-blue-500/20 transition-colors">
            <Send className="w-4 h-4" /> Skicka{companyEmail ? ` till ${companyEmail}` : ''}
          </button>
          </>
        )}
      </div>

      {companyInvoice && companyInvoice.users?.length > 0 && (
        <div className="space-y-4">
          <div className="flex items-center justify-between px-4 py-3 rounded-xl" style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.05)' }}>
            <div>
              <h3 className="text-lg font-bold text-white">{companyInvoice.company}</h3>
              <p className="text-xs text-white/30">{companyInvoice.period_label}</p>
            </div>
            <div className="text-right">
              <p className="stat-number text-2xl font-bold text-[#6dbf4a]">{companyInvoice.grand_total?.toLocaleString("sv-SE")} kr</p>
              <p className="text-xs text-white/30">{companyInvoice.grand_hours?.toFixed(1)} timmar totalt</p>
            </div>
          </div>

          {companyInvoice.users.map(pu => (
            <Card key={pu.user_id} className="bg-[#0d1218] border-white/[0.06]">
              <CardContent className="pt-4 pb-3">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="text-sm font-semibold text-white">{pu.name} <span className="text-xs text-white/25 font-normal ml-1">{pu.hourly_rate} kr/h</span></h4>
                  <span className="stat-number text-sm font-bold text-[#6dbf4a]">{pu.grand_total?.toLocaleString("sv-SE")} kr</span>
                </div>
                <div className="rounded-xl overflow-hidden border border-white/[0.04]">
                  <table className="w-full text-xs">
                    <thead><tr className="bg-white/[0.03]">
                      <th className="text-left px-3 py-1.5 text-white/30 font-medium">Datum</th>
                      <th className="text-left px-3 py-1.5 text-white/30 font-medium">Projekt</th>
                      <th className="text-left px-3 py-1.5 text-white/30 font-medium">Aktivitet</th>
                      <th className="text-right px-3 py-1.5 text-white/30 font-medium">Timmar</th>
                      <th className="text-right px-3 py-1.5 text-white/30 font-medium">Mil</th>
                    </tr></thead>
                    <tbody>
                      {pu.entries.map((e, i) => (
                        <tr key={i} className="border-t border-white/[0.03]">
                          <td className="px-3 py-1 text-white/50">{e.date}</td>
                          <td className="px-3 py-1 text-white">{e.project}</td>
                          <td className="px-3 py-1 text-white/50">{e.activity}</td>
                          <td className="px-3 py-1 text-right text-white font-mono">{e.hours.toFixed(1)}</td>
                          <td className="px-3 py-1 text-right text-white/40 font-mono">{e.mileage > 0 ? e.mileage.toFixed(1) : ''}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="flex justify-between mt-2 text-xs text-white/40">
                  <span>{pu.total_hours.toFixed(1)}h x {pu.hourly_rate} kr = {pu.total?.toLocaleString("sv-SE")} kr</span>
                  {pu.total_mileage > 0 && <span>+ Mil: {pu.mileage_pay?.toLocaleString("sv-SE")} kr</span>}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {companyInvoice && companyInvoice.users?.length === 0 && (
        <p className="text-center py-8 text-white/25">Inga godkanda tidsposter for denna period</p>
      )}

      {!companyInvoice && companies.length === 0 && (
        <p className="text-center py-8 text-white/25">Inga UE-foretag registrerade. Lagg till "Foretag" pa UE-anvandare under Anstallda.</p>
      )}
    </div>
  );
};
